
import { create } from 'zustand';
import type { Notification } from '@/entities/notification';
import { notificationsApi, apiErrorText } from '@/shared/api';
import { toast } from '@/shared/ui';
import { notificationsCache } from '../api/queries';

interface NotificationsState {
  pageItems: Notification[];
  pageTotal: number;
  pageLoading: boolean;
  pageError: string | null;
  pageLoaded: boolean;
  pendingNew: number;
  noteIncoming: () => void;
  fetchPageList: (params: {
    limit: number;
    offset: number;
    unreadOnly?: boolean;
  }) => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markTicketAsRead: (ticketId: string) => Promise<void>;
  activeTicketId: string | null;
  setActiveTicketId: (ticketId: string | null) => void;
  reset: () => void;
}

let pageRequestId = 0;

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  pageItems: [],
  pageTotal: 0,
  pageLoading: false,
  pageError: null,
  pageLoaded: false,
  pendingNew: 0,

  noteIncoming: () => set((s) => ({ pendingNew: s.pendingNew + 1 })),

  fetchPageList: async ({ unreadOnly = false, ...params }) => {
    const requestId = ++pageRequestId;
    set({ pageLoading: true, pageError: null });
    try {
      const res = await notificationsApi.list({ ...params, unreadOnly });
      if (requestId !== pageRequestId) return;
      const data = res as unknown as { data?: Notification[]; total?: number };
      const pageItems = Array.isArray(data.data) ? data.data : [];
      const pageTotal = typeof data.total === 'number' ? data.total : 0;
      set({ pageItems, pageTotal, pageLoading: false, pageLoaded: true, pendingNew: 0 });
      notificationsCache.refetchUnreadCount();
    } catch (err) {
      if (requestId !== pageRequestId) return;
      set({
        pageLoading: false,
        pageLoaded: true,
        pageError: apiErrorText(err, 'Не удалось загрузить уведомления'),
      });
    }
  },

  markAsRead: async (id) => {
    const known = get().pageItems.find((n) => n.id === id);
    if (known && (known.readAt || known.read)) return;
    try {
      await notificationsApi.markAsRead(id);
      const now = new Date().toISOString();
      set((s) => ({
        pageItems: s.pageItems.map((n) =>
          n.id === id ? { ...n, readAt: now, read: true } : n
        ),
      }));
      notificationsCache.removeOne(id);
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось отметить уведомление прочитанным'));
    }
  },

  markTicketAsRead: async (ticketId) => {
    try {
      await notificationsApi.markTicketAsRead(ticketId);
      const now = new Date().toISOString();
      set((s) => ({
        pageItems: s.pageItems.map((n) =>
          n.ticketId === ticketId ? { ...n, readAt: n.readAt ?? now, read: true } : n
        ),
      }));
      notificationsCache.removeByTicket(ticketId);
      notificationsCache.refetchUnreadCount();
    } catch {
    }
  },

  markAllAsRead: async () => {
    try {
      await notificationsApi.markAllAsRead();
      const now = new Date().toISOString();
      set((s) => ({
        pageItems: s.pageItems.map((n) => ({ ...n, readAt: n.readAt ?? now, read: true })),
      }));
      notificationsCache.clear();
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось отметить уведомления прочитанными'));
    }
  },

  activeTicketId: null,
  setActiveTicketId: (ticketId) => set({ activeTicketId: ticketId }),

  reset: () => {
    set({
      pageItems: [],
      pageTotal: 0,
      pageError: null,
      pageLoaded: false,
      pendingNew: 0,
      activeTicketId: null,
    });
    notificationsCache.reset();
  },
}));
